/**
 * FormField — wrapper accessibile per i campi form del portale paziente (PR4).
 *
 * Compone label + input/select/textarea + errore inline con aria-describedby.
 * Volutamente minimale: il portale e' un bundle separato, non riusiamo i
 * componenti UI complessi del gestionale per non gonfiare il bundle.
 *
 * Touch target: input padding garantisce >=44px alti su mobile.
 */

import { forwardRef } from 'react';
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

interface FieldRowProps {
  id: string;
  label: string;
  error?: string;
  hint?: string;
  required?: boolean;
  readOnly?: boolean;
  children: (ariaProps: {
    id: string;
    'aria-invalid'?: boolean;
    'aria-describedby'?: string;
    'aria-required'?: boolean;
  }) => ReactNode;
}

export function FieldRow({
  id,
  label,
  error,
  hint,
  required,
  readOnly,
  children,
}: FieldRowProps) {
  const errorId = error ? `${id}-error` : undefined;
  const hintId = hint ? `${id}-hint` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-text-main">
        {label}
        {required && (
          <span className="text-error ml-1" aria-hidden="true">
            *
          </span>
        )}
        {readOnly && (
          <span className="ml-2 text-xs text-text-secondary font-normal">
            (precompilato)
          </span>
        )}
      </label>
      {hint && (
        <p id={hintId} className="text-xs text-text-secondary">
          {hint}
        </p>
      )}
      {children({
        id,
        'aria-invalid': Boolean(error) || undefined,
        'aria-describedby': describedBy,
        'aria-required': required || undefined,
      })}
      {error && (
        <p id={errorId} role="alert" className="text-xs text-error">
          {error}
        </p>
      )}
    </div>
  );
}

const baseInput =
  'min-h-[44px] w-full rounded-lg border border-outline bg-surface-container-lowest px-3 py-2 text-base text-text-main placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-surface-container disabled:cursor-not-allowed read-only:bg-surface-container read-only:cursor-default aria-[invalid=true]:border-error aria-[invalid=true]:focus:ring-error';

// forwardRef obbligatorio: react-hook-form usa `ref` di register() per
// agganciarsi al nodo DOM. Senza forwardRef React 18 scarta il ref e RHF
// non riesce a tracciare i valori → al submit i campi sono `undefined` e
// Zod risponde "expected string" su ogni campo.
export const TextField = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }
>(function TextField(props, ref) {
  const { className, invalid, ...rest } = props;
  return (
    <input
      ref={ref}
      type={rest.type ?? 'text'}
      aria-invalid={invalid || undefined}
      className={`${baseInput} ${className ?? ''}`}
      {...rest}
    />
  );
});

export const TextArea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }
>(function TextArea(props, ref) {
  const { className, invalid, rows = 3, ...rest } = props;
  return (
    <textarea
      ref={ref}
      rows={rows}
      aria-invalid={invalid || undefined}
      className={`${baseInput} min-h-[88px] ${className ?? ''}`}
      {...rest}
    />
  );
});

export const SelectField = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & {
    invalid?: boolean;
    children: ReactNode;
  }
>(function SelectField(props, ref) {
  const { className, invalid, children, ...rest } = props;
  return (
    <select
      ref={ref}
      aria-invalid={invalid || undefined}
      className={`${baseInput} ${className ?? ''}`}
      {...rest}
    >
      {children}
    </select>
  );
});

interface CheckboxFieldProps {
  id: string;
  label: ReactNode;
  error?: string;
  required?: boolean;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

export function CheckboxField({
  id,
  label,
  error,
  required,
  checked,
  onChange,
  disabled,
}: CheckboxFieldProps) {
  const errorId = error ? `${id}-error` : undefined;
  return (
    <div className="flex flex-col gap-1">
      <label
        htmlFor={id}
        className="flex items-start gap-3 cursor-pointer select-none min-h-[44px] py-2"
      >
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          aria-required={required || undefined}
          aria-invalid={Boolean(error) || undefined}
          aria-describedby={errorId}
          className="mt-1 h-5 w-5 rounded border-outline text-primary focus:ring-primary"
        />
        <span className="text-sm text-text-main leading-snug">
          {label}
          {required && (
            <span className="text-error ml-1" aria-hidden="true">
              *
            </span>
          )}
        </span>
      </label>
      {error && (
        <p id={errorId} role="alert" className="text-xs text-error ml-8">
          {error}
        </p>
      )}
    </div>
  );
}
